import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Lock, Settings } from 'lucide-react';
import { useConsent } from '../../stores/consent';
import { useAudienceStore } from '../../stores/audience';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';

interface ToggleProps {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}

function Toggle({ id, checked, onChange, disabled = false }: ToggleProps) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={cn(
        'relative h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 focus-visible:ring-offset-2',
        checked ? 'bg-brand-600' : 'bg-stone-200',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      <span
        className={cn(
          'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,.2)] transition-transform duration-200 ease-out',
          checked ? 'left-[22px]' : 'left-0.5',
        )}
      />
    </button>
  );
}

const CATEGORIES = [
  {
    id: 'essentials',
    label: 'Essentiels',
    description:
      'Nécessaires au bon fonctionnement du site (session anonyme, CSRF, profil audience pour personnaliser la navigation). Ne peuvent pas être désactivés.',
    required: true,
  },
  {
    id: 'preferences',
    label: 'Préférences',
    description:
      'Mémorisent votre commune localement (session + appareil) pour afficher les aides de proximité. Stockage local uniquement — non transmis au serveur.',
    required: false,
  },
  {
    id: 'analytics',
    label: "Mesure d'audience",
    description:
      'PostHog — analyses anonymisées des pages visitées. Aucune donnée personnelle transmise à des tiers. Hébergement EU.',
    required: false,
  },
  {
    id: 'marketing',
    label: 'Marketing',
    description:
      'Publicités ciblées. Actuellement désactivé — aucun cookie publicitaire déposé par Solution Éco France.',
    required: false,
  },
] as const;

type ConsentCategoryId = 'essentials' | 'preferences' | 'analytics' | 'marketing';

export function CookieBanner() {
  const {
    hasResponded,
    essentials,
    preferences,
    analytics,
    marketing,
    customizeOpen,
    acceptAll,
    rejectAll,
    setCategories,
    openCustomize,
    closeCustomize,
  } = useConsent();

  // Ordre d'onboarding : consentement CNIL d'abord, personnalisation ensuite.
  // On attend uniquement l'hydratation pour éviter un flash avant lecture du stockage.
  const audienceHydrated = useAudienceStore((s) => s.hydrated);
  const canShowBanner = audienceHydrated;

  const [localPrefs, setLocalPrefs] = useState({ preferences, analytics, marketing });

  useEffect(() => {
    setLocalPrefs({ preferences, analytics, marketing });
  }, [preferences, analytics, marketing, customizeOpen]);

  const handleSaveCustom = () => {
    setCategories(localPrefs);
  };

  const toggle = (key: 'preferences' | 'analytics' | 'marketing') => {
    setLocalPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  const isChecked = (id: ConsentCategoryId): boolean => {
    if (id === 'essentials') return essentials;
    return localPrefs[id as 'preferences' | 'analytics' | 'marketing'];
  };

  return (
    <>
      {/* ── Bandeau CNIL ── */}
      <AnimatePresence>
        {!hasResponded && canShowBanner && !customizeOpen && (
          <motion.div
            role="region"
            aria-label="Gestion des cookies"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.3, ease: easeStripe }}
            className="fixed bottom-4 left-4 right-4 z-[60] mx-auto max-w-2xl"
          >
            <div className="rounded-2xl border border-stone-200/70 bg-paper p-4 shadow-floating ring-1 ring-inset ring-stone-100/60 sm:p-5">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-stone-100">
                  <Cookie className="h-4 w-4 text-stone-600" strokeWidth={1.5} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-ink">Vos préférences cookies</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-stone-600">
                    Nous utilisons des cookies pour mémoriser votre profil et mesurer l'audience
                    (PostHog, hébergement EU). Vous pouvez tout accepter, tout refuser ou
                    personnaliser vos choix.{' '}
                    <a
                      href="/legal/cookies"
                      className="underline underline-offset-2 hover:text-ink"
                    >
                      Politique cookies
                    </a>
                  </p>
                </div>
              </div>

              {/* ── 3 boutons d'égale visibilité (CNIL) ── */}
              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                <Button type="button" variant="secondary" size="lg" onClick={rejectAll}>
                  Tout refuser
                </Button>
                <Button type="button" variant="secondary" size="lg" onClick={openCustomize}>
                  <Settings className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Personnaliser
                </Button>
                <Button type="button" variant="primary" size="lg" onClick={acceptAll}>
                  Tout accepter
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Sheet personnalisation ── */}
      <Sheet open={customizeOpen} onOpenChange={(v) => !v && closeCustomize()}>
        <SheetContent side="right" className="flex w-full max-w-md flex-col overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Personnaliser mes cookies</SheetTitle>
            <SheetDescription>
              Choisissez les catégories que vous acceptez. Vos préférences sont sauvegardées sur
              votre appareil.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-1 overflow-y-auto px-5 py-4">
            {CATEGORIES.map((cat) => (
              <div
                key={cat.id}
                className="flex items-start gap-4 rounded-xl border border-stone-200/70 p-4 transition-colors hover:bg-stone-50/50"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <label
                      htmlFor={`consent-${cat.id}`}
                      className="cursor-pointer text-sm font-semibold text-ink"
                    >
                      {cat.label}
                    </label>
                    {cat.required && (
                      <span className="inline-flex items-center gap-0.5 rounded-md bg-stone-100 px-1.5 py-0.5 text-[10px] font-semibold text-stone-500">
                        <Lock className="h-2.5 w-2.5" />
                        Requis
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-stone-500">{cat.description}</p>
                </div>
                <Toggle
                  id={`consent-${cat.id}`}
                  checked={isChecked(cat.id)}
                  onChange={
                    cat.required
                      ? () => {}
                      : () => toggle(cat.id as 'preferences' | 'analytics' | 'marketing')
                  }
                  disabled={cat.required}
                />
              </div>
            ))}
          </div>

          <SheetFooter>
            <Button type="button" variant="secondary" size="lg" onClick={rejectAll}>
              Tout refuser
            </Button>
            <Button type="button" variant="primary" size="lg" onClick={handleSaveCustom}>
              Enregistrer mes choix
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
